import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MorphCanvas, type MorphPhase } from './MorphCanvas';

/**
 * The stage composition the preview mounts: the real artifact underneath, the morph canvas on top.
 *
 * While the phase is `'band'`/`'wobble'` the canvas is fully opaque — the card IS the band, there is
 * nothing under it worth seeing yet. The host mounts `children` (the real `ArtifactDocument`) at
 * resolve start; the canvas keeps covering it while the shader converges on the exact render (its
 * last 6% is literally the target pixels), and only once `onResolved` fires does the canvas fade out
 * over 240ms and unmount. So the handover is double-insured: the shader lands on the real pixels AND
 * the real document is already painted beneath before a single frame of it shows through.
 *
 * The veil is the only state here, and it moves three times per generation — solid at run start,
 * fading at resolve, gone 240ms later. Everything per-frame stays inside `MorphCanvas`'s rAF.
 * Unmounting at `'gone'` is what enforces the one-GL-context budget on /deck: the canvas exists only
 * while there is a wait or a resolve to show.
 */

export interface MorphResolveStageProps {
  width: number;
  height: number;
  phase: MorphPhase;
  fromTexture?: HTMLCanvasElement | null;
  toTexture?: HTMLCanvasElement | null;
  partsMap?: { delays: Uint8Array; count: number } | null;
  resolveMs?: number;
  /** Relayed after the stage has armed its own fade, so the host sees the same moment the veil does. */
  onResolved?: () => void;
  /** Relayed after the canvas is dropped — the host may want to skip straight to the document. */
  onFallback?: () => void;
  /** The real artifact. Mounted by the host at resolve start; sits underneath the canvas. */
  children?: React.ReactNode;
}

/** How long the canvas takes to reveal the already-identical document beneath it. */
const FADE_MS = 240;

/**
 * How far past its bottom edge the veil paints, in SCREEN pixels.
 *
 * The stage is always drawn scaled (the preview, a directory tile), so its height lands on a fraction of
 * a pixel. The GPU canvas and the DOM document beneath round that edge differently, and during the
 * resolve the white document showed as a hairline under the dark veil. Painting the veil a little past
 * the edge covers it; every host frame clips its stage, so the bleed itself is never seen.
 */
const BLEED_SCREEN_PX = 2;

type Veil = 'solid' | 'fading' | 'gone';

export function MorphResolveStage({
  width,
  height,
  phase,
  fromTexture,
  toTexture,
  partsMap,
  resolveMs,
  onResolved,
  onFallback,
  children,
}: MorphResolveStageProps) {
  const [veil, setVeil] = useState<Veil>('solid');
  const fadeTimerRef = useRef(0);
  const stageRef = useRef<HTMLDivElement>(null);
  // The bleed in the stage's own units: two screen pixels at whatever scale the host draws it.
  const [bleed, setBleed] = useState(0);
  useLayoutEffect(() => {
    const drawn = stageRef.current?.getBoundingClientRect().height ?? 0;
    if (drawn > 0) setBleed(Math.ceil((BLEED_SCREEN_PX * height) / drawn));
  }, [height, phase]);

  // A new wait re-arms the veil: the same stage instance can carry generation after generation, and
  // each one starts with the canvas back at full opacity before its first band frame draws.
  useEffect(() => {
    if (phase === 'band' || phase === 'morph-out' || phase === 'wobble') {
      window.clearTimeout(fadeTimerRef.current);
      setVeil('solid');
    }
  }, [phase]);

  useEffect(() => () => window.clearTimeout(fadeTimerRef.current), []);

  const handleResolved = (): void => {
    // Opacity is the ONE property that transitions, and it flips once — the fade is compositor-only,
    // and after it the canvas unmounts entirely rather than idling at opacity 0 with a live context.
    setVeil('fading');
    fadeTimerRef.current = window.setTimeout(() => setVeil('gone'), FADE_MS);
    onResolved?.();
  };

  const handleFallback = (): void => {
    // No WebGL → no ceremony. Drop the veil immediately so the document is simply there.
    setVeil('gone');
    onFallback?.();
  };

  const showCanvas = phase !== 'idle' && veil !== 'gone';

  return (
    <div ref={stageRef} style={{ position: 'relative', width, height }}>
      {children}
      {showCanvas && (
        <div
          style={{
            position: 'absolute',
            inset: `0 0 ${-bleed}px 0`,
            opacity: veil === 'fading' ? 0 : 1,
            transition: `opacity ${FADE_MS}ms ease`,
            // The band is a picture of progress, never a control — the document beneath must become
            // interactive the moment it exists, not 240ms later.
            pointerEvents: 'none',
          }}
        >
          <MorphCanvas
            width={width}
            height={height + bleed}
            phase={phase}
            fromTexture={fromTexture}
            toTexture={toTexture}
            partsMap={partsMap}
            resolveMs={resolveMs}
            onResolved={handleResolved}
            onFallback={handleFallback}
          />
        </div>
      )}
    </div>
  );
}
